import { Table, Popconfirm, Tooltip } from "antd";
import { CheckCircleFilled, MinusCircleFilled, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import * as React from "react";
import { FormattedMessage } from "react-intl";
import { getFrontendRoleNamesFromIds } from "@utils/applicationUtils";
import { TableIcon } from "@components/table/tableStyle";
import { Link } from "react-router-dom";
import { Flex } from "@components/flex/flex";
import { TableComponentProps, TableData, TableHandlers } from "@components/table/tableContainer";
import { ExportContentContainer } from "@components/export/exportContentContainer";
import Column from "antd/lib/table/Column";
import { ColumnProps } from "antd/lib/table";
import { ApplicationFragment } from "../../graphql2/types";

export interface ContentTableData extends TableData {
    title: string;
    type?: string | null;
    status: boolean;
    statusType: string;
    languages: string;
    roles: string[];
}

export interface ContentTableProps extends TableComponentProps {
    dataSource: ContentTableData[];
    handlers: TableHandlers & {
        removeContent(id: string): void;
    };
    applications?: ApplicationFragment[];
}

interface Handlers {
    children: JSX.Element;
    props: ColumnProps<ContentTableData>;
}

export const ContentTable: React.FC<ContentTableProps> = ({ handlers, applications, ...tableProps }) => {
    const renderTitle = (text: string, id: string) => (
        <Link className="plainText" to={`/content/contents/edit/${id}/general`}>
            {text}
        </Link>
    );

    const renderStatus = (status, statusType: string) => {
        if (status && statusType === "active") {
            return (
                <Tooltip title={statusType}>
                    <CheckCircleFilled className="green" />
                </Tooltip>
            );
        }

        return (
            <Tooltip title={statusType}>
                <MinusCircleFilled className={statusType} />
            </Tooltip>
        );
    };

    const renderHandlers = (row: ContentTableData): Handlers => ({
        children: (
            <Flex justifyContent="flex-end" alignItems="center">
                <ExportContentContainer id={row.id} />
                <Link to={`/content/contents/edit/${row.id}/general`}>
                    <TableIcon as={EditOutlined} />
                </Link>
                <Popconfirm title={<FormattedMessage id="deleteConfirm" />} onConfirm={() => handlers.removeContent(row.id)} okText="Yes" cancelText="No">
                    <TableIcon as={DeleteOutlined} />
                </Popconfirm>
            </Flex>
        ),
        props: {
            colSpan: 2
        }
    });

    const renderRoles = (row: ContentTableData): string => {
        if (applications) {
            return getFrontendRoleNamesFromIds(applications, row.roles).join(", ");
        }
        return "";
    };

    return (
        <Table
            pagination={{ showSizeChanger: true, showQuickJumper: true }}
            rowKey="id"
            {...tableProps}
        >
            <Column
                key="expiresOn"
                title={<FormattedMessage id="status" />}
                dataIndex="status"
                width={95}
                render={(text, row: ContentTableData) => renderStatus(text, row.statusType)}
                sorter
            />
            <Column
                key="title"
                title={<FormattedMessage id="title" />}
                dataIndex="title"
                sorter
                width={190}
                render={(text, row: ContentTableData) => renderTitle(text, row.id)}
            />
            <Column
                key="roles"
                title={<FormattedMessage id="roles" />}
                width={300}
                dataIndex="roles"
                render={(_, row: ContentTableData) => renderRoles(row)}
            />
            <Column
                key="type"
                title={<FormattedMessage id="type" />}
                dataIndex="type"
                sorter
                width={95}
            />
            <Column
                key="languages"
                title={<FormattedMessage id="languages" />}
                dataIndex="languages"
                width={95}
            />
            <Column
                title=" "
                key="handlers"
                dataIndex=""
                width={120}
                render={(_, row: ContentTableData) => renderHandlers(row)}
            />
        </Table>
    );
};
